import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import people from "../../../../assets/svg/ellipse.svg";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import {
  getAllUsers,
  getCustomersByRoutePartnerId,
} from "../../../../helper/functions";
import { toast } from "react-toastify";
import { addFundWallet } from "../../../../helper/campaign";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import { addedByConversion } from "../../../Shipping/ShippingService/shippingService.helper";

const initialValues = {
  forAllCustomers: false,
  forAllUsers: false,
  emails: [],
  restrictUsage: false,
  amount: 0,
  isAppliedToAllRoutePartners: false,
  appliedRoutePartners: [],
  publicNote: "",
  internalNote: "",
};

const validationSchema = Yup.object().shape({
  amount: Yup.number()
    .required("Amount is required")
    .min(0.01, "Amount must be greater than 0"),
  appliedRoutePartners: Yup.array().when("isAppliedToAllRoutePartners", {
    is: false,
    then: (schema) =>
      schema
        .min(1, "Please select at least one Route Partner")
        .required("Route Partners selection is required"),
  }),
});

const FundWallet = ({ setAddFundDrawer }) => {
  const [customerMails, setCustomerMails] = useState([]);
  const [routePartners, setRoutePartners] = useState([]);
  const user = useSelector((state) => state.auth.value);

  useEffect(() => {
    try {
      if (!user) return;
      const querySnapshot = collection(db, "routePartners");
      if (user?.role === "Superadmin") {
        const q = query(
          querySnapshot,
          where("isDeleted", "==", false),
          where("createdBy", "==", user?.id || ""),
        );
        onSnapshot(q, (snapshot) => {
          const arr = [];
          snapshot.forEach((doc) => {
            arr.push({ ...doc.data(), id: doc.id });
          });
          setRoutePartners(arr);
        });
      } else if (user?.role === "Route Partner Admin") {
        const q = query(
          querySnapshot,
          where("isDeleted", "==", false),
          where("__name__", "==", `${user?.routePartnerId}`),
        );
        onSnapshot(q, (snapshot) => {
          const arr = [];
          snapshot.forEach((doc) => {
            arr.push({ ...doc.data(), id: doc.id });
          });
          setRoutePartners(arr);
        });
      }
    } catch (err) {
      console.log(err, "err");
    }
  }, [user]);

  useEffect(() => {
    const getCustomers = async () => {
      const customers = await getCustomersByRoutePartnerId(
        user?.routePartnerId,
      );
      setCustomerMails(customers || []);
    };
    const getUsers = async () => {
      const users = await getAllUsers();
      setCustomerMails(users || []);
    };

    if (user) {
      if (user?.role === "Route Partner Admin") {
        getCustomers();
      } else {
        getUsers();
      }
    }
  }, [user]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const isAllUsers = values.forAllCustomers || values.forAllUsers;
        const isAllRoutePartners = values.isAppliedToAllRoutePartners;

        // Resolve full userIds list: all from state if "for all", else from selected emails
        const userIds = isAllUsers
          ? customerMails.map((u) => u.id).filter(Boolean)
          : values.emails.map((e) => e.userId);
        console.log(userIds, "userIds");
        // Resolve full routePartnerIds list: all from state if "for all", else from selected
        const routePartnerIds = isAllRoutePartners
          ? routePartners.map((rp) => rp.id).filter(Boolean)
          : values.appliedRoutePartners.map((r) => r.id);

        if (!userIds.length)
          throw new Error(
            "No users found to apply funds to. Please check your selection.",
          );
        if (!routePartnerIds.length)
          throw new Error(
            "No route partners found to apply funds to. Please check your selection.",
          );

        // Build transactionNote based on route partner selection
        const transactionNote = isAllRoutePartners
          ? "Applies to all partners."
          : `Applies only to ${values.appliedRoutePartners.map((rp) => rp.name).join(", ")}.`;

        // Build full payload for the campaigns collection
        const campaignEmails = isAllUsers
          ? []
          : values.emails.map((e) => ({ email: e.email, userId: e.userId }));
        const offeredBy =
          user?.role === "Superadmin" ? "POSTPAL" : user?.routePartnerName;

        const campaignData = {
          ...values,
          emails: campaignEmails,
          userIds: isAllUsers ? [] : values.emails.map((e) => e.userId),
          campaignType: "fund_wallet",
          addedByUserId: user?.id || "",
          addedBy: addedByConversion(user?.role),
          routePartnerId: user?.routePartnerId || "",
          offeredBy,
        };

        await addFundWallet({
          isAllRoutePartners,
          userIds,
          routePartnerIds,
          amount: values.amount,
          note: transactionNote,
          campaignData,
        });

        setAddFundDrawer(false);
        toast.success("Funds added to wallet successfully");
      } catch (err) {
        console.error("addFundWallet error:", err);
        toast.error(err?.message || "Failed to add funds. Please try again.");
      }
    },
  });

  const emailOptions = customerMails.map((option) => ({
    email: option.email,
    userId: option.id,
  }));

  const routePartnerOptions = routePartners.map((rp) => ({
    id: rp.id,
    name: rp.name,
  }));

  return (
    <Box>
      {user?.role === "Route Partner Admin" && (
        <Box sx={{ paddingX: "20px" }}>
          <FormControlLabel
            label="For all customers"
            control={
              <Checkbox
                size="small"
                onChange={(event) => {
                  if (event.target.checked) {
                    formik.setFieldValue("emails", []);
                  }
                  formik.setFieldValue("forAllCustomers", event.target.checked);
                }}
              />
            }
          />
        </Box>
      )}
      {user?.role !== "Route Partner Admin" && (
        <Box sx={{ paddingX: "20px" }}>
          <FormControlLabel
            label="For all users"
            control={
              <Checkbox
                size="small"
                onChange={(event) => {
                  if (event.target.checked) {
                    formik.setFieldValue("emails", []);
                  }
                  formik.setFieldValue("forAllUsers", event.target.checked);
                }}
              />
            }
          />
        </Box>
      )}
      <Box sx={{ paddingX: "20px", paddingTop: "10px" }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Email
        </Typography>
        <FormControl fullWidth>
          <Autocomplete
            multiple
            size="small"
            options={emailOptions}
            getOptionLabel={(option) => option.email}
            value={formik.values.emails}
            disabled={
              formik.values.forAllCustomers || formik.values.forAllUsers
            }
            onChange={(event, newValue) => {
              formik.setFieldValue("emails", newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select emails"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.email}
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
          />
        </FormControl>
      </Box>
      {/* <Box
                sx={{
                    paddingX: "20px",
                }}
            >
                <FormControlLabel
                    label="Restrict promo usage to only selected users"
                    control={
                        <Checkbox
                            size="small"
                            checked={formik?.values?.restrictUsage}
                            onChange={(event) => {
                                formik.setFieldValue("restrictUsage", event.target.checked)
                            }}
                        />
                    }
                />
            </Box> */}
      <Box
        sx={{
          paddingX: "20px",
          paddingTop: "20px",
        }}
      >
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Amount <span style={{ color: "red" }}>*</span>
        </Typography>
        <FormControl fullWidth>
          <OutlinedInput
            id="outlined-adornment-amount"
            type="number"
            value={formik?.values?.amount}
            onChange={(event) =>
              formik.setFieldValue("amount", +event.target.value)
            }
            onBlur={formik.handleBlur}
            startAdornment={<InputAdornment position="start">$</InputAdornment>}
            error={formik.touched.amount && Boolean(formik.errors.amount)}
            sx={{
              fontWeight: "bold",
              fontSize: "24px", // or 32px based on AddFundDrawer.jsx standard
            }}
          />
          {formik.touched.amount && formik.errors.amount && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, display: "block" }}
            >
              {formik.errors.amount}
            </Typography>
          )}
        </FormControl>
      </Box>

      <Box sx={{ paddingX: "20px", paddingTop: "20px" }}>
        <FormControlLabel
          label="For all Route partner"
          control={
            <Checkbox
              size="small"
              checked={formik?.values?.isAppliedToAllRoutePartners}
              onChange={(event) => {
                formik.setFieldValue(
                  "isAppliedToAllRoutePartners",
                  event.target.checked,
                );
                if (event.target.checked) {
                  formik.setFieldValue("appliedRoutePartners", []);
                }
              }}
            />
          }
        />
      </Box>
      <Box sx={{ paddingX: "20px", paddingTop: "10px" }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Applies to Route Partners{" "}
          {!formik?.values?.isAppliedToAllRoutePartners && (
            <span style={{ color: "red" }}>*</span>
          )}
        </Typography>
        <FormControl fullWidth>
          <Autocomplete
            multiple
            size="small"
            options={routePartnerOptions}
            getOptionLabel={(option) => option.name}
            disabled={formik?.values?.isAppliedToAllRoutePartners}
            value={formik?.values?.appliedRoutePartners || []}
            onChange={(event, newValue) => {
              formik.setFieldValue("appliedRoutePartners", newValue);
            }}
            onBlur={formik.handleBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select Route Partners"
                error={
                  formik.touched.appliedRoutePartners &&
                  Boolean(formik.errors.appliedRoutePartners)
                }
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.name}
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
          />
          {formik.touched.appliedRoutePartners &&
            formik.errors.appliedRoutePartners && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                {formik.errors.appliedRoutePartners}
              </Typography>
            )}
        </FormControl>
      </Box>

      <Box sx={{ marginX: "20px", paddingTop: "20px" }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Public Note
        </Typography>
        <TextField
          name="publicNote"
          variant="outlined"
          size="small"
          fullWidth
          onChange={(event) =>
            formik.setFieldValue("publicNote", event.target.value)
          }
          value={formik?.values?.publicNote}
        />
      </Box>
      <Box sx={{ marginX: "20px", paddingTop: "20px" }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Internal Note
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={5}
          name="internalNote"
          value={formik?.values?.internalNote}
          onChange={(event) =>
            formik.setFieldValue("internalNote", event.target.value)
          }
        />
      </Box>
      <Box sx={{ marginX: "20px", paddingTop: "10px", paddingBottom: "20px" }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          Applied by
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
          <img src={people} alt="people" width={24} height={24} />
          <Typography sx={{ marginLeft: "10px" }}>{user?.name}</Typography>
        </Box>
      </Box>
      {/* Fixed positioned button at bottom right */}
      <Box
        sx={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#481AA3",
            color: "#fff",
            border: "none",
            boxShadow: "0px 4px 12px rgba(72, 26, 163, 0.3)",
            padding: "12px 24px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            minWidth: "180px",
            height: "48px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#5E17EB",
              boxShadow: "0px 6px 16px rgba(72, 26, 163, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }}
          onClick={formik.handleSubmit}
          disabled={!formik.isValid || formik.isSubmitting}
        >
          Add funds to wallet
        </Button>
      </Box>
    </Box>
  );
};

export default FundWallet;
